import { useCallback, useEffect, useRef, useState } from "react";
import { productionGateway } from "./production-gateway";
import { appendOperatorHistory, type OperatorHistoryItem, type OperatorHistoryMode } from "./operator-history-data";

type HistoryState = {
  scope: string; items: OperatorHistoryItem[]; nextCursor: string | null;
  loading: boolean; loaded: boolean; error: string | null;
};
const emptyHistory = (scope: string): HistoryState => ({ scope, items: [], nextCursor: null, loading: false, loaded: false, error: null });

/** Reads bounded pages only while the history is open; polling never loads it. */
export function useOperatorHistory(input: {
  terminalCode: string; sessionId: string; runId: string | null; mode: OperatorHistoryMode; open: boolean;
}) {
  const { terminalCode, sessionId, runId, mode, open } = input;
  const scope = `${terminalCode}:${sessionId}:${runId ?? ""}:${mode}`;
  const [state, setState] = useState<HistoryState>(() => emptyHistory(scope));
  const stateRef = useRef(state);
  stateRef.current = state;
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const request = useRef<AbortController | null>(null);
  const sequence = useRef(0);
  const retryRequest = useRef<{ scope: string; cursor: string | null; append: boolean } | null>(null);

  const read = useCallback(async (cursor: string | null, append: boolean) => {
    if (!open || !runId || !sessionId || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    const id = ++sequence.current;
    const owns = () => !controller.signal.aborted && sequence.current === id && activeScope.current === scope;
    retryRequest.current = { scope, cursor, append };
    setState(current => ({ ...(current.scope === scope ? current : emptyHistory(scope)), loading: true, error: null }));
    try {
      const page = await productionGateway.terminalOperationalHistory(terminalCode, runId, { mode, cursor, limit: 50 }, controller.signal);
      if (!owns()) return;
      setState(current => ({ scope, items: append && current.scope === scope
        ? appendOperatorHistory(current.items, page.items) : page.items,
      nextCursor: page.nextCursor, loading: false, loaded: true, error: null }));
    } catch (error) {
      if (!owns()) return;
      setState(current => ({ ...(current.scope === scope ? current : emptyHistory(scope)), loading: false,
        error: error instanceof Error ? error.message : "Hareket geçmişi alınamadı. Yeniden deneyin." }));
    } finally {
      if (request.current === controller) request.current = null;
    }
  }, [mode, open, runId, scope, sessionId, terminalCode]);

  useEffect(() => {
    request.current?.abort(); request.current = null; sequence.current++;
    retryRequest.current = null;
    setState(emptyHistory(scope));
    if (open) void read(null, false);
    return () => { sequence.current++; request.current?.abort(); request.current = null; };
  }, [open, read, scope]);

  const refresh = useCallback(() => {
    request.current?.abort(); request.current = null; sequence.current++;
    return read(null, false);
  }, [read]);
  const loadMore = useCallback(() => {
    const current = stateRef.current;
    if (current.scope === scope && current.nextCursor) void read(current.nextCursor, true);
  }, [read, scope]);
  const retry = useCallback(() => {
    const failed = retryRequest.current;
    if (failed?.scope === scope) void read(failed.cursor, failed.append);
  }, [read, scope]);
  return { ...(state.scope === scope ? state : emptyHistory(scope)), refresh, loadMore, retry };
}
